import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslate } from 'react-admin'
import { useField } from 'formik'
import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import YouTubeIcon from '@material-ui/icons/YouTube'
import LiveTvIcon from '@material-ui/icons/LiveTv'
import DnsIcon from '@material-ui/icons/Dns'
import UndoIcon from '@material-ui/icons/Undo'
import streamFieldsStyles from './streamFields.styles'
import GAME_EVENT_FORM_BROADCAST_FIELDS from '../../../utils/constants/gameEventFormBroadcastFields'
import { isFormFieldDisabled } from '../../../utils/helpers/isFieldDisabled'

// YouTube's fixed RTMP ingest base. In the default (YouTube) mode the operator only edits the key
// that follows it; the custom-server toggle swaps to a full rtmp:// URL for streaming elsewhere.
export const YOUTUBE_RTMP_BASE = 'rtmp://a.rtmp.youtube.com/live2/'

const isRtmpUrl = (value) => /^rtmps?:\/\//i.test((value ?? '').trim())
// Pull the key out of a known ingest URL (YouTube's live2 or a generic /app/ path).
const extractKnownKey = (url) => {
  const match = (url ?? '').trim().match(/rtmps?:\/\/[^\s]*?\/(?:live2|app)\/(.+)$/i)
  return match ? match[1] : null
}
const extractYoutubeKey = (url) => {
  const match = (url ?? '').trim().match(/youtube\.com\/live2\/(.+)$/i)
  return match ? match[1] : null
}

const useStyles = makeStyles(streamFieldsStyles)

const StreamField = ({ state }) => {
  const translate = useTranslate()
  const classes = useStyles()
  const fieldName = GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION
  const [field, meta, helpers] = useField(fieldName)
  const disabled = isFormFieldDisabled(state, fieldName)
  const isError = Boolean(meta.touched && meta.error)

  // Split the stored value into the two editing modes once, on mount. A bare value is a YouTube
  // key; a full rtmp:// URL is either a YouTube ingest URL (→ key) or a custom server (→ custom).
  const initial = useMemo(() => {
    const raw = field.value ?? ''
    if (isRtmpUrl(raw)) {
      const ytKey = extractYoutubeKey(raw)
      if (ytKey) return { custom: false, key: ytKey, url: '' }
      return { custom: true, key: '', url: raw }
    }
    return { custom: false, key: raw, url: '' }
  }, [])

  const [custom, setCustom] = useState(initial.custom)
  const [key, setKey] = useState(initial.key)
  const [url, setUrl] = useState(initial.url)

  // Reflect external changes to the field value (e.g. "Reuse Setup" writing a stream key) that our
  // local state didn't produce — re-derive the mode/key/url so the inputs stay in sync.
  useEffect(() => {
    const raw = field.value ?? ''
    if (raw === (custom ? url : key)) return
    if (isRtmpUrl(raw)) {
      const ytKey = extractYoutubeKey(raw)
      if (ytKey) {
        setCustom(false)
        setKey(ytKey)
        setUrl('')
      } else {
        setCustom(true)
        setUrl(raw)
        setKey('')
      }
    } else {
      setCustom(false)
      setKey(raw)
      setUrl('')
    }
  }, [field.value])

  const commitKey = useCallback(
    (nextKey) => {
      setKey(nextKey)
      helpers.setValue(nextKey)
    },
    [helpers],
  )

  const commitUrl = useCallback(
    (nextUrl) => {
      setUrl(nextUrl)
      helpers.setValue(nextUrl)
    },
    [helpers],
  )

  // Pasting into the key field: a plain key pastes normally; a full rtmp URL is reduced to its key
  // when we recognise the server, otherwise it flips to custom mode carrying the full URL.
  const handleKeyPaste = useCallback(
    (e) => {
      const text = (e.clipboardData?.getData('text') ?? '').trim()
      if (!isRtmpUrl(text)) return
      e.preventDefault()
      const known = extractKnownKey(text)
      if (known) {
        commitKey(known)
      } else {
        setCustom(true)
        commitUrl(text)
      }
    },
    [commitKey, commitUrl],
  )

  const toggleCustom = useCallback(() => {
    if (disabled) return
    if (!custom) {
      // YouTube -> custom: start from an empty field for the custom server.
      setUrl('')
      setCustom(true)
      helpers.setValue('')
    } else {
      // custom -> YouTube: recover the key if it was a YouTube URL, else keep the previous key.
      const nextKey = extractYoutubeKey(url) ?? key
      setKey(nextKey)
      setCustom(false)
      helpers.setValue(nextKey)
    }
  }, [custom, url, key, disabled, helpers])

  const errorBorder = isError ? { borderColor: '#E06E5C' } : undefined

  return (
    <div className='div-flex-column' style={{ gap: 8 }}>
      {custom ? (
        <div className={classes.skCustomRow} style={errorBorder}>
          <LiveTvIcon className={classes.skCustomIcon} />
          <input
            className={classes.skCustomInput}
            value={url}
            disabled={disabled}
            placeholder={translate('ra.formFields.customServerPlaceholder')}
            onChange={(e) => commitUrl(e.target.value)}
            onBlur={field.onBlur}
          />
        </div>
      ) : (
        <div className={classes.skRow} style={errorBorder}>
          <div className={classes.skChip}>
            <YouTubeIcon className={classes.skChipIcon} />
            <span className={classes.skChipText}>YouTube</span>
          </div>
          <input
            className={classes.skKeyInput}
            value={key}
            disabled={disabled}
            placeholder={translate('ra.formFields.streamKeyPlaceholder')}
            onChange={(e) => commitKey(e.target.value)}
            onPaste={handleKeyPaste}
            onBlur={field.onBlur}
          />
        </div>
      )}

      <span
        role='button'
        tabIndex={disabled ? -1 : 0}
        onClick={toggleCustom}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            toggleCustom()
          }
        }}
        className={classes.serverToggle}
        style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
      >
        {custom ? (
          <UndoIcon className={classes.serverToggleIcon} />
        ) : (
          <DnsIcon className={classes.serverToggleIcon} />
        )}
        {translate(custom ? 'ra.gameDrawer.useYoutube' : 'ra.gameDrawer.streamToCustomServer')}
      </span>
    </div>
  )
}

StreamField.propTypes = {
  state: PropTypes.string,
}

export default StreamField
